import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { OptionDefinition } from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces";
import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  DEFAULT_OPTION,
  PAYMENT_METHOD_OPTIONS,
  STAGES,
  TIME_RANGES
} from "../config/recentTransactionTableConfig";
import { RecentTransactionsSearchParams, SearchFilters } from "../types/common";
import { Transaction } from "../lusca-search-api/generated-src";
import useFetchRecentTransactions from "./useFetchRecentTransactions";
import { GL_MAPPING } from "./useSummaryData";

export function useRecentTransactions() {
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const [currentServerPage, setCurrentServerPage] = useState(1);

  const getDefaultFilters = () => ({
    stage: STAGES[0],
    gl: DEFAULT_OPTION,
    marketplaces: [],
    usecases: [],
    paymentMethod: PAYMENT_METHOD_OPTIONS[0],
    timeRange: TIME_RANGES[1],
    date: "",
    consumptionTypes: [],
  });

  const [selectedFilters, setSelectedFilters] =
    useState<SearchFilters>(getDefaultFilters);

  const [apiSearchParams, setApiSearchParams] =
    useState<RecentTransactionsSearchParams>({
      stage: STAGES[0].value,
    });

  const getEndTime = (
    range: OptionDefinition,
    date: string
  ): number | undefined => {
    if (range.value === "custom" && date) {
      const dateAtNoon = new Date(date);
      dateAtNoon.setUTCHours(12, 0, 0, 0);
      return Math.floor(dateAtNoon.getTime() / 1000);
    }
    const now = Math.floor(Date.now() / 1000);
    switch (range.value) {
      case "5min":
        return now - 5 * 60;
      case "15min":
        return now - 15 * 60;
      case "1hour":
        return now - 60 * 60;
      case "24hours":
        return now - 24 * 60 * 60;
      case "1week":
        return now - 7 * 24 * 60 * 60;
      case "1month":
        return now - 30 * 24 * 60 * 60;
      default:
        return undefined;
    }
  };

  useEffect(() => {
    const stageParam = urlSearchParams.get("stage");
    const glParam = urlSearchParams.get("gl");
    const countriesParam = urlSearchParams.get("countries");
    const usecasesParam = urlSearchParams.get("usecases");
    const paymentMethodParam = urlSearchParams.get("paymentMethod");
    const timeRangeParam = urlSearchParams.get("timeRange");
    const dateParam = urlSearchParams.get("date");
    const consumptionTypesParam = urlSearchParams.get("consumptionTypes");

    const stageOption =
      STAGES.find((opt) => opt.value === stageParam) || STAGES[0];
    const glOption = glParam
      ? { value: glParam, label: GL_MAPPING[glParam] || glParam }
      : DEFAULT_OPTION;
    const paymentMethodOption =
      PAYMENT_METHOD_OPTIONS.find((pm) => pm.value === paymentMethodParam) ||
      PAYMENT_METHOD_OPTIONS[0];
    const timeRangeOption =
      TIME_RANGES.find((tr) => tr.value === timeRangeParam) || TIME_RANGES[1];
    const marketplaces = countriesParam
      ? countriesParam.split(",").map((c) => ({ value: c, label: c }))
      : [];
    const usecases = usecasesParam
      ? usecasesParam.split(",").map((u) => ({ value: u, label: u }))
      : [];
    const consumptionTypesOption = consumptionTypesParam
      ? consumptionTypesParam.split(",").map((ct)=>({ value: ct, label: ct}))
      : [];

    setSelectedFilters({
      stage: stageOption,
      gl: glOption,
      marketplaces: marketplaces,
      usecases: usecases,
      paymentMethod: paymentMethodOption,
      timeRange: timeRangeOption,
      date: dateParam || "",
      consumptionTypes: consumptionTypesOption
    })

    const newParams: RecentTransactionsSearchParams = {
      stage: stageOption.value,
      ...(glOption.value &&
        glOption.value !== DEFAULT_OPTION.value && { gl: glOption.value }),
      ...(marketplaces.length > 0 &&
        !marketplaces.some((m) => m.value === DEFAULT_OPTION.value) && {
          countries: marketplaces.map((m) => m.value),
        }),
      ...(usecases.length > 0 &&
        !usecases.some((u) => u.value === DEFAULT_OPTION.value) && {
          usecases: usecases.map((u) => u.value),
        }),
      ...(paymentMethodOption.value !== 'all' && {
        paymentMethod: paymentMethodOption.value!,
      }),
      endTime: getEndTime(timeRangeOption, dateParam || ""),
      ...(consumptionTypesOption.length > 0 &&
        !consumptionTypesOption.some((u) => u.value === DEFAULT_OPTION.value) && {
          consumptionTypes: consumptionTypesOption.map((u) => u.value),
        }),
    };

    setApiSearchParams(newParams);
    setCurrentServerPage(1);
    setAllTransactions([]);
    actions.setCurrentPage(1);
  }, [urlSearchParams]);

  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const { transactions, nextPageTimestamp, executedQuery, isLoading, error } =
    useFetchRecentTransactions(apiSearchParams);

  useEffect(() => {
    if (!isLoading && transactions.length > 0) {
      setAllTransactions((prev) => {
        const newTransactions = transactions.filter(
          (newTx) => !prev.some((prevTx) => prevTx.id === newTx.id)
        );
        return [...prev, ...newTransactions];
      });
    }
  }, [transactions, isLoading]);

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(allTransactions, {
    filtering: {
      filteringFunction: (item: Transaction, filteringText: string) => {
        if (!filteringText) return true;
        const filterText = filteringText.toLowerCase();
        const searchableValues = [
          item.id,
          item.stage,
          item.country,
          item.gl,
          GL_MAPPING[item.gl],
          item.usecase,
          item.paymentMethod,
          item.consumptionType,
          new Date(item.processedTime).toLocaleString(),
        ];
        return searchableValues.some((value) =>
          String(value).toLowerCase().includes(filterText)
        );
      },
    },
    pagination: { pageSize: 15 },
    sorting: {
      defaultState: {
        sortingColumn: { sortingField: "processedTime" },
        isDescending: true,
      },
    },
  });

  const isFiltering = !!filterProps.filteringText;
  const handleSearch = () => {

    const newParams: RecentTransactionsSearchParams = {
      stage: selectedFilters.stage.value!,
      ...(selectedFilters.gl?.value &&
        selectedFilters.gl.value !== DEFAULT_OPTION.value && { gl: selectedFilters.gl.value }),
      ...(selectedFilters.marketplaces.length > 0 &&
        !selectedFilters.marketplaces.some((m) => m.value === DEFAULT_OPTION.value) && {
          countries: selectedFilters.marketplaces
            .map((m) => m.value!)
            .filter((v): v is string =>
              Boolean(v && v !== DEFAULT_OPTION.value)
            ),
        }),
      ...(selectedFilters.usecases.length > 0 &&
        !selectedFilters.usecases.some((u) => u.value === DEFAULT_OPTION.value) && {
          usecases: selectedFilters.usecases
            .map((u) => u.value!)
            .filter((v): v is string =>
              Boolean(v && v !== DEFAULT_OPTION.value)
            ),
        }),
      ...(selectedFilters.paymentMethod.value !== "all" && {
        paymentMethod: selectedFilters.paymentMethod.value!
      }),
      ...(selectedFilters.consumptionTypes.length > 0 &&
        !selectedFilters.consumptionTypes.some((u) => u.value === DEFAULT_OPTION.value) && {
          consumptionTypes: selectedFilters.consumptionTypes
            .map((u) => u.value!)
            .filter((v): v is string =>
              Boolean(v && v !== DEFAULT_OPTION.value)
            ),
        }),
      endTime: getEndTime(selectedFilters.timeRange, selectedFilters.date),
    };

    const params: Record<string, string> = {};
    params.stage = selectedFilters.stage.value!;
    if (selectedFilters.gl.value != null && selectedFilters.gl.value !== DEFAULT_OPTION.value) {
      params.gl = selectedFilters.gl.value;
    }
    if (
      selectedFilters.marketplaces.length > 0 &&
      !selectedFilters.marketplaces.some((m) => m.value === DEFAULT_OPTION.value)
    ) {
      params.countries = selectedFilters.marketplaces.map((m) => m.value!).join(",");
    }
    if (
      selectedFilters.usecases.length > 0 &&
      !selectedFilters.usecases.some((u) => u.value === DEFAULT_OPTION.value)
    ) {
      params.usecases = selectedFilters.usecases.map((u) => u.value!).join(",");
    }
    if (selectedFilters.paymentMethod.value !== 'all') {
      params.paymentMethod = selectedFilters.paymentMethod.value!;
    }

    if (
      selectedFilters.consumptionTypes.length > 0 &&
      !selectedFilters.consumptionTypes.some((u) => u.value === DEFAULT_OPTION.value)
    ) {
      params.consumptionTypes = selectedFilters.consumptionTypes.map((u) => u.value!).join(",");
    }

    params.timeRange = selectedFilters.timeRange.value!;

    if (selectedFilters.date) {
      params.date = selectedFilters.date;
    }
    setUrlSearchParams(params);
    setApiSearchParams(newParams);
    setCurrentServerPage(1);
    setAllTransactions([]);
    actions.setCurrentPage(1);
  };

  const clearFilter = () => {
    actions.setFiltering("");
    setSelectedFilters(getDefaultFilters());
  };

  const handlePageChange = ({
    detail,
  }: {
    detail: { currentPageIndex: number };
  }) => {
    const newPage = detail.currentPageIndex;
    actions.setCurrentPage(newPage);
    if (newPage > currentServerPage && nextPageTimestamp && !isFiltering) {
      setCurrentServerPage(newPage);
      setApiSearchParams((prev) => ({
        ...prev,
        endTime: nextPageTimestamp,
      }));
    }
  };

  const pagesCount = (() => {
    if (isFiltering) return Math.ceil((filteredItemsCount || 0) / 15) || 1;
    if (isLoading && currentServerPage === 1) return 2;
    return nextPageTimestamp ? currentServerPage + 1 : currentServerPage;
  })();

  const customPaginationProps = {
    currentPageIndex: paginationProps.currentPageIndex,
    pagesCount,
    openEnd: !isFiltering,
    disabled: isLoading,
    onChange: handlePageChange,
  };

  return {
    items,
    actions,
    collectionProps,
    filterProps,
    paginationProps: customPaginationProps,
    handleSearch,
    isLoading,
    searchParams: apiSearchParams,
    clearFilter,
    selectedFilters,
    setSelectedFilters,
    executedQuery,
    error,
  };
}
